import React from 'react';
import { ViewState } from '../types';
import { 
  LayoutDashboard, 
  ShoppingCart, 
  Package, 
  Users, 
  BarChart3, 
  LogOut,
  Pill,
  Truck,
  Receipt,
  UserCheck,
  Settings as SettingsIcon
} from 'lucide-react';

interface LayoutProps {
  currentView: ViewState;
  onNavigate: (view: ViewState) => void;
  onLogout: () => void;
  children: React.ReactNode;
  storeName?: string;
  storePhone?: string;
  storeAddress?: string;
}

const Layout: React.FC<LayoutProps> = ({ 
  currentView, 
  onNavigate, 
  onLogout, 
  children,
  storeName = "Abdullah",
  storePhone = "12345",
  storeAddress = "Housing Society Sanghar"
}) => {
  
  const navItems = [
    { id: ViewState.DASHBOARD, label: 'Dashboard', icon: <LayoutDashboard size={20} /> },
    { id: ViewState.POS, label: 'POS Terminal', icon: <ShoppingCart size={20} /> },
    { id: ViewState.INVENTORY, label: 'Inventory', icon: <Package size={20} /> },
    { id: ViewState.PURCHASE, label: 'Purchase Orders', icon: <Truck size={20} /> },
    { id: ViewState.VENDORS, label: 'Vendors', icon: <Users size={20} /> },
    { id: ViewState.EXPENDITURE, label: 'Expenditures', icon: <Receipt size={20} /> },
    { id: ViewState.REPORTS, label: 'Reports & Accounts', icon: <BarChart3 size={20} /> },
    { id: ViewState.USERS, label: 'Staff Management', icon: <UserCheck size={20} /> },
    { id: ViewState.SETTINGS, label: 'Settings', icon: <SettingsIcon size={20} /> },
  ];
  
  return (
    <div className="flex h-screen overflow-hidden bg-slate-100">
      {/* Sidebar - Now always visible for consistent navigation */}
      <aside className="w-64 bg-slate-900 text-white flex flex-col shadow-xl z-20">
        <div className="p-6 border-b border-slate-800 flex items-center gap-3">
          <div className="bg-emerald-500 p-2 rounded-lg">
            <Pill size={24} className="text-white" />
          </div>
          <div>
            <h1 className="font-bold text-lg leading-tight">{storeName}</h1>
            <p className="text-xs text-slate-400">Medical Store</p>
          </div>
        </div>

        <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => onNavigate(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                currentView === item.id
                  ? 'bg-emerald-600 text-white shadow-lg'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
              }`}
            >
              {item.icon}
              <span className="font-medium">{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-800">
          <div className="bg-slate-800 rounded-lg p-4 mb-4">
            <p className="text-xs text-slate-400 mb-1">Store Contact</p>
            <p className="text-sm font-semibold text-white">{storePhone}</p>
            <p className="text-xs text-slate-400 truncate">{storeAddress}</p>
          </div>
          <button 
            onClick={onLogout}
            className="flex items-center gap-2 text-slate-400 hover:text-red-400 transition-colors w-full px-2"
          >
            <LogOut size={18} />
            <span className="text-sm">Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-auto relative">
        <header className="bg-white border-b border-slate-200 h-20 flex items-center justify-between px-8 sticky top-0 z-10">
            <h2 className="text-2xl font-bold text-slate-800">
              {navItems.find(n => n.id === currentView)?.label}
            </h2>
            <div className="flex items-center gap-4">
               <div className="text-right">
                  <p className="text-sm font-semibold text-slate-900 leading-tight">Abdullah Admin</p>
                  <p className="text-xs text-emerald-600 font-bold uppercase tracking-wider">Authorized Access</p>
               </div>
               <div className="h-11 w-11 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-bold border-2 border-white shadow-sm">
                 AA
               </div>
            </div>
        </header>
        <div className="flex-1 p-8 pb-20">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;